import { cn, NativeSelect, NativeSelectOptGroup, NativeSelectOption } from "@pdfking/components"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { NullQ } from "@pdfking/structs"
import { Items } from "@pdfking/tfunc"

import type { AnyChoice } from "../mixins/FormMixin"
import { SelectMixin } from "../mixins/SelectMixin"
import { input_class, TRAILING_ICON_INSET } from "./classes"

export type { AnyChoice }
export { SelectMixin }

export const DjSelect = CreateRenderFunction({
  mixins: [SelectMixin] as const,
  computed: {
    all_entries(): [string, string][] {
      return this.expanded_choices.flatMap(([, opts]) => Items(opts))
    },
    current_key(): string {
      return NullQ(this.input) ? "" : String(this.input)
    },
    invalid_choice(): boolean {
      if (this.current_key === "") return false
      return !this.all_entries.some(([k]) => k === this.current_key)
    },
  },
  props: {
    className: "",
    error: undefined as unknown,
    input: undefined as unknown,
    style: "",
  },
  render() {
    const options = (
      <>
        {this.invalid_choice && (
          <NativeSelectOption value={this.current_key}>{this.current_key}</NativeSelectOption>
        )}
        {this.expanded_choices.map(([label, opts]) =>
          label ? (
            <NativeSelectOptGroup key={label} label={label}>
              {Items(opts).map(([key, text]) => (
                <NativeSelectOption key={key} value={key}>
                  {text}
                </NativeSelectOption>
              ))}
            </NativeSelectOptGroup>
          ) : (
            Items(opts).map(([key, text]) => (
              <NativeSelectOption key={key} value={key}>
                {text}
              </NativeSelectOption>
            ))
          )
        )}
      </>
    )
    const disabled = this.disabled || (!this.invalid_choice && this.all_entries.length <= 1)

    return (
      // NativeSelect's own wrapper div is a fixed `w-fit` with no className hook
      // onto it (see @pdfking/components' vendored native-select.tsx) — stretch
      // it from here instead of touching the vendored file. `TRAILING_ICON_INSET`
      // repositions its caret from the same place, for the same reason.
      <div className={cn("[&_[data-slot=native-select-wrapper]]:w-full", TRAILING_ICON_INSET)}>
        <NativeSelect
          className={cn(input_class(this.invalid_choice || this.error), "pr-6", this.className)}
          disabled={disabled}
          id={this.dom_id}
          onInput={(e: Event) => this.onselect?.((e.target as HTMLSelectElement).value)}
          style={this.style}
          value={this.current_key}
        >
          {options}
        </NativeSelect>
      </div>
    )
  },
})
